import Image from 'next/image';
import NextLink from 'next/link';

import { Box, Button, Flex, Heading, LinkBox, LinkOverlay, Text } from '@chakra-ui/react';

import { RocketIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

import {
  FLASH_SALE_CTA_LABEL,
  FLASH_SALE_HEADLINE,
  FLASH_SALE_RAYS_BG,
  FLASH_SALE_SUBTITLE,
  getFlashSaleCheckoutHref,
} from '../constants';
import type { FlashTier } from '../utils/get-flash-tier';

import { FlashSalePillEyebrow } from './flash-sale-pill-eyebrow';

export type FlashSaleBannerProps = {
  tier: FlashTier;
  /** Live Braavos coupon from the promotion response; appended to the CTA as `?coupon=`. */
  promoCode?: string | null;
  /** Eager-load the art when the banner is the first/visible carousel slide. */
  imagePriority?: boolean;
  onCtaClick?: () => void;
};

function FlashSaleCta() {
  return (
    <Button
      alignItems='center'
      as='span'
      backgroundColor='blue.500'
      borderRadius='4px'
      color='white'
      display='inline-flex'
      flexShrink={0}
      fontSize='12px'
      fontWeight='400'
      height='auto'
      leftIcon={<RocketIcon boxSize='16px' color='white' aria-hidden />}
      lineHeight={1.2}
      paddingX='14px'
      paddingY='10px'
      pointerEvents='none'
      tabIndex={-1}
      variant='unstyled'
      aria-hidden
    >
      {FLASH_SALE_CTA_LABEL}
    </Button>
  );
}

function FlashSaleBackground({ imagePriority }: { imagePriority?: boolean }) {
  return (
    <Box backgroundColor='#000b26' bottom={0} left={0} pointerEvents='none' position='absolute' right={0} top={0}>
      <Image
        alt=''
        loading='eager'
        priority={imagePriority}
        sizes='(max-width: 1024px) 100vw, 1024px'
        src={FLASH_SALE_RAYS_BG}
        style={{ objectFit: 'cover', objectPosition: 'right center' }}
        fill
      />
      <Box
        bgGradient='linear(245deg, rgba(0, 41, 140, 0) 25%, rgba(0, 11, 38, 0.95) 77%)'
        bottom={0}
        left={0}
        position='absolute'
        right={0}
        top={0}
      />
    </Box>
  );
}

/**
 * Catalog value-props flash banner — a horizontal (`space-between`) layout that stacks on mobile.
 * The homepage flash surface uses `FlashSalePromoCard` (shared `PromoCard`); `/experience` uses its
 * own ATF card, so this component is catalog-only.
 */
export function FlashSaleBanner({ tier, promoCode, imagePriority, onCtaClick }: FlashSaleBannerProps) {
  const headline = FLASH_SALE_HEADLINE[tier];
  const ariaLabel = `${headline}: ${FLASH_SALE_CTA_LABEL}`;

  return (
    <LinkBox
      borderRadius='8px'
      height='100%'
      isolation='isolate'
      minHeight={{ base: 'auto', md: '120px' }}
      overflow='hidden'
      position='relative'
      width='100%'
    >
      <FlashSaleBackground imagePriority={imagePriority} />
      <Flex
        flexDirection='column'
        gap={{ base: 3, md: '10px' }}
        height='100%'
        justifyContent='center'
        padding={{ base: '20px', md: '16px 24px' }}
        position='relative'
        zIndex={1}
      >
        <FlashSalePillEyebrow />
        <Flex
          alignItems={{ base: 'flex-start', md: 'center' }}
          flexDirection={{ base: 'column', md: 'row' }}
          gap={{ base: 3, md: 4 }}
          justifyContent='space-between'
          width='100%'
        >
          <Box minWidth={0}>
            <Heading
              as='h3'
              color='white'
              fontSize={{ base: '1.25rem', md: '1.5rem' }}
              fontWeight='600'
              lineHeight={1.15}
            >
              {headline}
            </Heading>
            <Text color='cyan.300' fontSize={{ base: '1rem', md: '1.125rem' }} fontWeight='600' lineHeight={1.5}>
              {FLASH_SALE_SUBTITLE}
            </Text>
          </Box>
          <FlashSaleCta />
        </Flex>
      </Flex>
      <LinkOverlay
        aria-label={ariaLabel}
        as={NextLink}
        href={getFlashSaleCheckoutHref(tier, promoCode)}
        inset={0}
        position='absolute'
        zIndex={2}
        onClick={onCtaClick}
      />
    </LinkBox>
  );
}
